/**
 * HUD: scoreboard, clock, boost meter, countdown, goal banner.
 *
 * DOM overlay rather than canvas. Text rendered by the browser is sharper at
 * any DPI than anything we would blit into a texture, it costs no draw calls,
 * and it scales with `devicePixelRatio` for free. The cost is that the styling
 * has to be written deliberately — a DOM HUD with default typography looks like
 * a form, which is the one thing it must not look like.
 *
 * TYPOGRAPHY RULES ENFORCED HERE:
 *  - every numeral is tabular (`font-variant-numeric: tabular-nums`), so the
 *    clock does not jitter as digits change width. This is the single most
 *    visible detail in the whole overlay.
 *  - one type scale, one weight ramp, uppercase tracked labels.
 *  - nothing is centred by eye: the score row is a three-column grid with the
 *    clock in the middle, so it stays optically centred at any score.
 *
 * Every update is diffed against the last value written, so a frame that
 * changes nothing touches no DOM at all.
 */

import type { MatchPhase } from '../game/match';
import { DEFAULT_BINDINGS, type KeyBindings } from '../input/controller';
import { keyLabel } from '../ui/bindings';
import { TEAM_COLORS } from './materials';

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Boost is shown as an integer 0-100; the physics value is fractional. */
export const formatBoost = (boost: number): number => {
  if (!Number.isFinite(boost)) return 0;
  return Math.max(0, Math.min(100, Math.round(boost)));
};

/**
 * `stroke-dashoffset` for a boost ring of the given circumference. Full boost
 * is offset 0 (the whole arc drawn); empty is the full circumference.
 */
export const boostDashOffset = (boost: number, circumference: number): number => {
  const t = Math.max(0, Math.min(1, (Number.isFinite(boost) ? boost : 0) / 100));
  return circumference * (1 - t);
};

/** `#rrggbb` for a packed colour, for writing into inline styles. */
export const cssHex = (color: number): string => `#${(color >>> 0).toString(16).padStart(6, '0')}`;

// ---------------------------------------------------------------------------
// Markup and style
// ---------------------------------------------------------------------------

const BOOST_RADIUS = 54;
const BOOST_CIRCUMFERENCE = 2 * Math.PI * BOOST_RADIUS;

/**
 * Most the firework wash may ever reach. The effect's own `wash` is a physical
 * 0..1 — how much of the display is burning — and this is the separate question
 * of how much of that the screen is allowed to take. Measured in the browser:
 * anything above about a fifth stops being a light and starts being a filter.
 */
const GLOW_CEILING = 0.2;

const STYLE = `
.rw-hud {
  position: absolute;
  inset: 0;
  pointer-events: none;
  user-select: none;
  z-index: 10;
  font-family: 'Inter', 'SF Pro Display', 'Segoe UI Variable Display', system-ui,
    -apple-system, 'Helvetica Neue', Arial, sans-serif;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1, 'cv01' 1;
  color: #eef4ff;
  -webkit-font-smoothing: antialiased;
}
.rw-hud * { box-sizing: border-box; font-variant-numeric: tabular-nums; }

.rw-top {
  position: absolute;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  display: grid;
  grid-template-columns: 116px 132px 116px;
  align-items: stretch;
  gap: 4px;
  filter: drop-shadow(0 6px 18px rgba(0, 0, 0, 0.55));
}
.rw-score {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 62px;
  font-size: 38px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  background: rgba(9, 13, 22, 0.82);
  border-top: 3px solid currentColor;
  backdrop-filter: blur(6px);
}
.rw-score-0 { clip-path: polygon(0 0, 100% 0, 100% 100%, 14px 100%); }
.rw-score-1 { clip-path: polygon(0 0, 100% 0, calc(100% - 14px) 100%, 0 100%); }
.rw-score span { color: #f2f7ff; }

.rw-clock {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 62px;
  gap: 2px;
  background: rgba(5, 8, 14, 0.88);
  backdrop-filter: blur(6px);
}
.rw-clock-time {
  font-size: 30px;
  font-weight: 650;
  letter-spacing: 0.01em;
  line-height: 1;
  transition: color 300ms linear;
}
/* The clock borrows the boost meter's amber under thirty seconds and goes
   red-and-breathing under ten. Regulation only — overtime counts UP and a
   red 0:07 there would read as "almost over" when it means the opposite. */
.rw-clock-time.late { color: #ffc23a; }
.rw-clock-time.critical { color: #ff5a4e; animation: rw-clock-pulse 1s ease-in-out infinite; }
@keyframes rw-clock-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }
.rw-clock-label {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(190, 208, 235, 0.72);
}

.rw-boost {
  position: absolute;
  right: 46px;
  bottom: 40px;
  width: 132px;
  height: 132px;
  filter: drop-shadow(0 6px 18px rgba(0, 0, 0, 0.5));
}
/* One quick swell when a pad tops the meter up — the pickup is felt on the
   pitch but the NUMBER is peripheral, and the motion is what carries it. */
@keyframes rw-boost-pop {
  0% { transform: scale(1); }
  35% { transform: scale(1.07); }
  100% { transform: scale(1); }
}
.rw-boost.pop { animation: rw-boost-pop 340ms cubic-bezier(0.16, 1, 0.3, 1); }
.rw-boost svg { width: 100%; height: 100%; transform: rotate(-90deg); }
.rw-boost-track { fill: none; stroke: rgba(255, 255, 255, 0.10); stroke-width: 9; }
.rw-boost-arc {
  fill: none;
  stroke: #ffc23a;
  stroke-width: 9;
  stroke-linecap: round;
  transition: stroke 120ms linear;
}
.rw-boost-value {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.rw-speedo {
  position: absolute;
  right: 196px;
  bottom: 64px;
  text-align: right;
  filter: drop-shadow(0 6px 18px rgba(0, 0, 0, 0.5));
  transition: opacity 200ms ease-out;
}
.rw-speedo-value {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  transition: color 140ms linear, text-shadow 140ms linear;
}
.rw-speedo-label {
  margin-top: 3px;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(190, 208, 235, 0.72);
  transition: color 140ms linear;
}
.rw-speedo.super .rw-speedo-value {
  color: #bfe1ff;
  text-shadow: 0 0 14px rgba(120, 190, 255, 0.85);
}
.rw-speedo.super .rw-speedo-label {
  color: rgba(159, 208, 255, 0.95);
}

.rw-centre {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.rw-countdown {
  font-size: 148px;
  font-weight: 800;
  letter-spacing: -0.06em;
  line-height: 1;
  opacity: 0;
  text-shadow: 0 10px 44px rgba(0, 0, 0, 0.7);
}
/* The kickoff "GO": pops harder than a number and gets out of the way fast —
   it lands on the exact frame control is handed over, and the one thing it
   must not do is sit over the ball. */
@keyframes rw-go {
  0% { opacity: 0; transform: scale(0.72); }
  14% { opacity: 1; transform: scale(1.12); }
  60% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.08); }
}
.rw-countdown.go { animation: rw-go 640ms cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.rw-banner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  opacity: 0;
  transform: translateY(14px);
  transition: opacity 180ms ease-out, transform 260ms cubic-bezier(0.16, 1, 0.3, 1);
}
.rw-banner.on { opacity: 1; transform: translateY(0); }
.rw-banner-title {
  font-size: 92px;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 0.95;
  text-transform: uppercase;
  text-shadow: 0 8px 40px rgba(0, 0, 0, 0.75);
}
.rw-banner-sub {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(226, 236, 255, 0.86);
}

/* The replay card. Broadcast language: a live-red dot, a tracked label, and
   the two facts worth knowing — who, and how hard. Top left, which is the one
   corner the scoreboard, the boost ring and the hint strip all leave alone. */
.rw-replay {
  position: absolute;
  top: 22px;
  left: 26px;
  min-width: 214px;
  padding: 12px 16px 13px;
  background: rgba(6, 9, 16, 0.86);
  border-left: 3px solid #ff3b30;
  backdrop-filter: blur(6px);
  opacity: 0;
  transform: translateX(-10px);
  transition: opacity 200ms ease-out, transform 320ms cubic-bezier(0.16, 1, 0.3, 1);
  filter: drop-shadow(0 6px 18px rgba(0, 0, 0, 0.55));
}
.rw-replay.on { opacity: 1; transform: translateX(0); }
.rw-replay-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #ffffff;
}
.rw-replay-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff3b30;
  box-shadow: 0 0 10px rgba(255, 59, 48, 0.9);
  animation: rw-pulse 1.1s ease-in-out infinite;
}
@keyframes rw-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }
.rw-replay-scorer {
  margin-top: 8px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.1;
}
.rw-replay-stat {
  margin-top: 2px;
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(190, 208, 235, 0.7);
}
.rw-replay-stat b {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #eef4ff;
}
.rw-replay-bar {
  margin-top: 11px;
  height: 2px;
  background: rgba(255, 255, 255, 0.14);
  overflow: hidden;
}
.rw-replay-bar i { display: block; height: 100%; width: 0%; background: #ff3b30; }
.rw-replay-skip {
  margin-top: 9px;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(198, 214, 240, 0.46);
}

/* Arena wash from the fireworks: a burst tints the room for a moment.
   Deliberately weak. Screen-blending a saturated team colour over the whole
   frame at anything approaching full opacity does not read as light, it reads
   as a broken post-process — the first build of this was orange enough to hide
   the pitch. It sits high in the frame, where the shells are, and fades out
   well before the bottom third so the car and the HUD stay legible. */
.rw-glow {
  position: absolute;
  inset: 0;
  opacity: 0;
  mix-blend-mode: screen;
  background: radial-gradient(
    ellipse 80% 55% at 50% 8%,
    currentColor 0%,
    rgba(0, 0, 0, 0) 70%
  );
}
.rw-flash {
  position: absolute;
  inset: 0;
  background: #ffffff;
  opacity: 0;
  mix-blend-mode: screen;
}
.rw-speed {
  position: absolute;
  inset: 0;
  opacity: 0;
  background: radial-gradient(
    ellipse at 50% 55%,
    rgba(255, 255, 255, 0) 34%,
    rgba(180, 214, 255, 0.10) 62%,
    rgba(120, 170, 255, 0.42) 100%
  );
}
/* The held scoreboard. A dark slab over the middle of the frame — it is read
   while standing still, so it is allowed to cover the pitch, and it fades
   rather than snapping so releasing the key does not feel like a cut. */
.rw-sb {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) scale(0.985);
  display: grid;
  grid-template-columns: repeat(2, minmax(300px, 360px));
  gap: 2px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.10);
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease-out, transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
  filter: drop-shadow(0 18px 50px rgba(0, 0, 0, 0.7));
}
.rw-sb.on { opacity: 1; transform: translate(-50%, -50%) scale(1); }
.rw-sb-team { background: rgba(7, 10, 17, 0.94); padding: 0 0 6px; }
.rw-sb-team-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 11px 14px 9px;
  border-top: 3px solid currentColor;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.26em;
  text-transform: uppercase;
}
.rw-sb-team[data-team='0'] { color: #2a7dff; }
.rw-sb-team[data-team='1'] { color: #ff8a1f; }
.rw-sb-team-head b {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #f2f7ff;
}
/* One grid for the head and every body row, so the columns cannot drift. */
.rw-sb-row {
  display: grid;
  grid-template-columns: 1fr repeat(5, 30px) 42px;
  align-items: center;
  gap: 2px;
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #dbe6f7;
}
.rw-sb-row span { text-align: right; }
.rw-sb-name { text-align: left !important; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rw-sb-head {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(160, 182, 214, 0.66);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding-bottom: 6px;
}
.rw-sb-score { font-weight: 700; color: #f2f7ff; }
/* Your own row is the one you look for first. */
.rw-sb-row.you { background: rgba(255, 255, 255, 0.07); color: #ffffff; }
/* The leader gets a mark, not a colour: the row colours already mean team. */
.rw-sb-row.leader .rw-sb-name::after {
  content: '★';
  margin-left: 7px;
  font-size: 11px;
  color: #ffc23a;
}

/* Wrecked. Sits under the centre of the frame rather than over it: the player
   is a spectator for two seconds and the pitch is the thing worth watching,
   so this states what happened and gets out of the way. */
.rw-wrecked {
  position: absolute;
  left: 50%;
  top: 58%;
  transform: translate(-50%, 0) scale(0.94);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 160ms ease-out, transform 260ms cubic-bezier(0.16, 1, 0.3, 1);
  text-shadow: 0 6px 26px rgba(0, 0, 0, 0.8);
}
.rw-wrecked.on { opacity: 1; transform: translate(-50%, 0) scale(1); }
.rw-wrecked-title {
  font-size: 34px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #ff6a5e;
}
.rw-wrecked-sub {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: rgba(214, 228, 250, 0.72);
}
.rw-wrecked-sub b {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0;
  color: #eef4ff;
}

/* The off-screen ball arrow. Rides the edge of the frame at whatever bearing
   the ball is on, so a player who has lost it knows which way to turn without
   switching to ball cam. Deliberately small and half-lit: it is a nudge, not a
   waypoint marker, and a bright chevron on the edge of the pitch pulls the eye
   away from the thing it is pointing at. */
.rw-ball-arrow {
  position: absolute;
  left: 0;
  top: 0;
  width: 34px;
  height: 34px;
  margin: -17px 0 0 -17px;
  opacity: 0;
  color: #cfe0ff;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.7));
  transition: opacity 160ms linear;
}
.rw-ball-arrow.on { opacity: 0.72; }
.rw-ball-arrow svg { width: 100%; height: 100%; display: block; }

.rw-hint {
  position: absolute;
  left: 26px;
  bottom: 26px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(198, 214, 240, 0.5);
  line-height: 1.7;
}

/* --- practice ------------------------------------------------------------
   Top left, which is the only corner a match does not already use: the score
   and clock are centred, the boost ring and speed are bottom right, and the
   key hints are bottom left. */
.rw-drill {
  position: absolute;
  top: 22px;
  left: 26px;
  min-width: 176px;
  padding: 10px 14px 12px;
  background: rgba(9, 13, 22, 0.82);
  border-left: 3px solid currentColor;
  backdrop-filter: blur(6px);
  filter: drop-shadow(0 6px 18px rgba(0, 0, 0, 0.5));
}
.rw-drill[hidden] { display: none; }
.rw-drill-name {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: rgba(198, 214, 240, 0.72);
}
.rw-drill-stats {
  margin-top: 6px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 14px;
}
.rw-drill-attempt { font-size: 21px; font-weight: 700; letter-spacing: -0.02em; }
.rw-drill-hits {
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.78);
}
/* The attempt clock. A bar and not a number: it is read peripherally, while
   the player is looking at a ball somewhere else on the screen. */
.rw-drill-bar {
  margin-top: 9px;
  height: 3px;
  background: rgba(255, 255, 255, 0.1);
}
.rw-drill-bar i {
  display: block;
  height: 100%;
  width: 100%;
  background: currentColor;
  transform-origin: left center;
}
.rw-drill.low i { background: #ff8a5c; }

/* --- the touch layout ---------------------------------------------------
   Set by \`setTouchLayout\` when the on-screen controls are mounted (see
   src/input/touch.ts). Both bottom corners become thumbs — the stick on the
   left, the button cluster on the right — so the two readouts that live down
   there move up out of the way, and the key hints go entirely: a strip
   reading "W/S THROTTLE" on a device with no W is worse than no strip. */
.rw-hud[data-touch='on'] .rw-hint { display: none; }
/* Clear of the on-screen pause button, which owns that corner. The offset is
   a literal because the touch unit variable lives on the overlay's own root
   and does not inherit here — see src/input/touch.ts. */
.rw-hud[data-touch='on'] .rw-drill { left: 88px; top: 14px; }
.rw-hud[data-touch='on'] .rw-boost {
  top: 92px;
  right: 20px;
  bottom: auto;
  width: 96px;
  height: 96px;
}
.rw-hud[data-touch='on'] .rw-boost-value { font-size: 25px; }
.rw-hud[data-touch='on'] .rw-speedo {
  top: 196px;
  right: 26px;
  bottom: auto;
}
.rw-hud[data-touch='on'] .rw-speedo-value { font-size: 22px; }
/* A landscape phone is short before it is narrow: the score block and the
   boost ring together are taller than the room above the thumbs. */
@media (max-height: 430px) {
  .rw-hud[data-touch='on'] .rw-boost { top: 76px; width: 78px; height: 78px; }
  .rw-hud[data-touch='on'] .rw-boost-value { font-size: 21px; }
  .rw-hud[data-touch='on'] .rw-speedo { top: 162px; }
}
`;

// ---------------------------------------------------------------------------
// Control hints
// ---------------------------------------------------------------------------

/** Non-breaking spaces hug the middot, so a wrap can never leave a lone
 *  separator hanging at the end of a line. */
const HINT_SEPARATOR = ' \u00a0·\u00a0 ';

/**
 * The hint strip, as (actions, what they do) rather than as literal key caps.
 *
 * It used to be the string "W / S throttle · A / D steer · SPACE jump …", which
 * is correct for exactly one binding map: the default one. The moment a player
 * rebound anything the HUD started lying, and a HUD that lies about the controls
 * is worse than one with no hints at all. Rows are rendered from the LIVE
 * bindings, and `Hud.setBindings` re-renders them when the pause menu applies a
 * rebind mid-match.
 */
const HINT_ROWS: readonly { actions: readonly (keyof KeyBindings)[]; label: string }[] = [
  { actions: ['throttleForward', 'throttleReverse'], label: 'throttle' },
  { actions: ['steerLeft', 'steerRight'], label: 'steer' },
  { actions: ['jump'], label: 'jump' },
  { actions: ['boost'], label: 'boost' },
  { actions: ['handbrake'], label: 'powerslide' },
  { actions: ['ballCam'], label: 'ball cam' },
];

/**
 * Appended to the hint strip. Not a `HINT_ROWS` entry because it is not a
 * BINDING — the scoreboard is on a fixed key (see `HeldKey` in ./scoreboard),
 * so deriving it from `KeyBindings` would be deriving it from nothing.
 */
const SCOREBOARD_HINT = 'TAB scores';

/**
 * The replay's skip line, in the markup and here so `setTouchLayout` can put
 * it back. Not a binding — `SkipLatch` fires on ANY input (see
 * src/render/replay.ts), which is why the line names no key.
 */
const REPLAY_SKIP_HINT = 'Press any key to skip';

/** What the practice panel shows. See `Hud.setDrill`. */
export interface DrillHudState {
  readonly name: string;
  /** 1-based. */
  readonly attempt: number;
  readonly attempts: number;
  readonly hits: number;
  /** Attempt clock, 1 at the start and 0 when it has run out. */
  readonly remaining: number;
}

/**
 * The hint strip for a binding map. A row whose every action is unbound is
 * dropped rather than printed as "— jump": the player cleared it deliberately,
 * and an empty cap reads as a rendering bug.
 */
export const controlHintText = (bindings: KeyBindings = DEFAULT_BINDINGS): string => {
  const parts: string[] = [];
  for (const row of HINT_ROWS) {
    const caps = row.actions
      .map(action => bindings[action]?.[0] ?? '')
      .filter(code => code.length > 0)
      .map(keyLabel);
    if (caps.length === 0) continue;
    parts.push(`${caps.join(' / ')} ${row.label}`);
  }
  parts.push(SCOREBOARD_HINT);
  return parts.join(HINT_SEPARATOR);
};

// ---------------------------------------------------------------------------
// HUD
// ---------------------------------------------------------------------------

/** What the replay card shows. Null whenever no replay is playing. */
export interface HudReplayState {
  /** Who to credit. See `scorerLabel` in ./replay. */
  scorer: string;
  /** Ball speed as it crossed the line, uu/s. */
  speed: number;
  /** 0..1 through the replay, for the progress bar. */
  progress: number;
}

export interface HudState {
  score: readonly [number, number];
  /** Pre-formatted clock, e.g. `MatchState.formatClock()`. */
  clock: string;
  /** Raw seconds remaining, for the urgency tint. `MatchState.clock`. */
  clockSeconds: number;
  phase: MatchPhase;
  overtime: boolean;
  /** 0..100, or null to hide the ring (spectating: no car is "yours"). */
  boost: number | null;
  /** 3, 2, 1 during the countdown; 0 when hidden. */
  countdown: number;
  /** Set for the whole goal/replay window; null otherwise. */
  goal: { team: 0 | 1; speed: number } | null;
  /** 0..1 white-out from the goal explosion. */
  flash: number;
  /** 0..1 speed-line intensity. */
  speed: number;
  /** The replay card, or null when nothing is being replayed. */
  replay: HudReplayState | null;
  /**
   * The player car's speed readout, or null to hide it (replays and bot-vs-bot
   * spectating, where "your" speed is nobody's speed).
   */
  speedo: { speed: number; supersonic: boolean } | null;
  /** The off-screen ball arrow's placement, or null when the ball is visible. */
  ballArrow: BallPointer | null;
  /** Seconds until the player's own car is back, or null while they are alive. */
  respawnIn: number | null;
  /**
   * The held scoreboard's markup, or null when the key is not down. Passed as
   * markup rather than as rows because building it is the caller's job and it
   * is only built on the frames the board is actually open.
   */
  scoreboard: string | null;
  /** 0..1 team-tinted arena wash from the fireworks. */
  glow: number;
}

/**
 * The banner and the replay card both name the shot speed, and they must agree
 * to the digit — two different roundings of the same number, eight lines apart
 * on screen, reads as one of them being wrong.
 */
export const formatSpeed = (speed: number): string =>
  `${Number.isFinite(speed) ? Math.max(0, Math.round(speed)) : 0}`;

/**
 * The wrecked banner's countdown text, or '' when the player is alive.
 *
 * Rounded UP, so a timer of 2.4s reads "3" and the number the player is
 * looking at never reaches 0 while they are still waiting — a respawn counter
 * that sits on zero for half a second reads as a hang.
 */
export const respawnCountdown = (secondsRemaining: number | null): string => {
  if (secondsRemaining === null || !Number.isFinite(secondsRemaining)) return '';
  return secondsRemaining <= 0 ? '' : String(Math.max(1, Math.ceil(secondsRemaining)));
};

/** Where the off-screen ball arrow sits, in CSS space. See `ballPointer`. */
export interface BallPointer {
  /** 0..1 across the viewport, +x right. */
  x: number;
  /** 0..1 down the viewport, +y DOWN (CSS convention). */
  y: number;
  /** Degrees clockwise from "up", for a glyph whose rest pose points up. */
  angle: number;
}

/**
 * Where to draw the off-screen ball arrow, or null when the ball is visible.
 *
 * Inputs are normalized device coordinates from the camera projection: x and y
 * in [-1, 1] with +y UP, plus `behind` for a ball behind the camera plane
 * (`ndc.z > 1`), where the projection mirrors x and y through the origin and
 * the raw numbers point the wrong way entirely.
 *
 * The result is already in CSS space — +y down, an angle in degrees — so the
 * caller writes it straight into `left`, `top` and `rotate()`. That split is
 * deliberate: the projection needs a camera and cannot be tested in Node, but
 * the "which edge, pointing where" arithmetic is where the bugs live, and it
 * is all here.
 */
export const ballPointer = (
  ndcX: number,
  ndcY: number,
  behind: boolean,
  margin = 0.06
): BallPointer | null => {
  if (!Number.isFinite(ndcX) || !Number.isFinite(ndcY)) return null;

  let x = behind ? -ndcX : ndcX;
  let y = behind ? -ndcY : ndcY;

  // On screen and in front: no arrow. The ball itself is the indicator.
  if (!behind && Math.abs(x) <= 1 && Math.abs(y) <= 1) return null;

  // Push onto the edge of the unit square. Dividing by the larger component
  // handles both cases with one formula: an off-screen ball (max > 1) comes
  // IN to the edge, and a ball directly behind (max < 1 after mirroring) goes
  // OUT to it.
  const extent = Math.max(Math.abs(x), Math.abs(y));
  if (extent < 1e-6) {
    // Dead centre behind the camera — no direction to derive, so point down:
    // the ball is at your back, and down is the shortest read of "turn round".
    x = 0;
    y = -1;
  } else {
    x /= extent;
    y /= extent;
  }

  const inset = Math.max(0, Math.min(0.45, Number.isFinite(margin) ? margin : 0));
  const reach = 0.5 - inset;
  const angle = (Math.atan2(x, y) * 180) / Math.PI;
  return {
    x: 0.5 + x * reach,
    y: 0.5 - y * reach,
    angle: (angle + 360) % 360,
  };
};

/**
 * The clock's urgency, from the raw seconds. Regulation only: overtime counts
 * UP from zero, so its small numbers mean the opposite of scarce — and a
 * finished match freezes at 0:00, which must not sit there flashing red.
 */
export type ClockTone = 'normal' | 'late' | 'critical';
export const clockTone = (seconds: number, overtime: boolean, phase: MatchPhase): ClockTone =>
  overtime || phase === 'ended'
    ? 'normal'
    : seconds <= 10
      ? 'critical'
      : seconds <= 30
        ? 'late'
        : 'normal';

/**
 * Whether a boost write is a PICKUP rather than a drain or a tick of idle.
 * A small pad is worth 12, so anything ≥ 10 in one write is a pad (or a
 * respawn's refill, which deserves the same nudge).
 */
export const boostPickedUp = (prev: number | undefined, next: number): boolean =>
  typeof prev === 'number' && prev >= 0 && next - prev >= 10;

/**
 * What the countdown numeral shows for a value, given the one it replaced.
 * "GO" happens exactly on the 1 -> 0 edge: a fresh HUD (prev undefined) or a
 * goal reset (0 -> 3) must not flash it.
 */
export const countdownDisplay = (n: number, prev: number | undefined): string =>
  n > 0 ? String(n) : typeof prev === 'number' && prev > 0 ? 'GO' : '';

/**
 * The speedometer's two lines. The label doubles as the supersonic tell: the
 * unit line flips to the word itself, because a colour change alone is
 * invisible to anyone who has not memorised the palette.
 */
export const speedoReadout = (
  speed: number,
  supersonic: boolean
): { value: string; label: string } => ({
  value: formatSpeed(speed),
  label: supersonic ? 'Supersonic' : 'uu/s',
});

export class Hud {
  readonly root: HTMLDivElement;

  private readonly scoreEls: [HTMLElement, HTMLElement];
  private readonly clockEl: HTMLElement;
  private readonly clockLabel: HTMLElement;
  private readonly boostEl: HTMLElement;
  private readonly boostArc: SVGCircleElement;
  private readonly boostValue: HTMLElement;
  private readonly speedoEl: HTMLElement;
  private readonly speedoValue: HTMLElement;
  private readonly speedoLabel: HTMLElement;
  private readonly countdownEl: HTMLElement;
  private readonly bannerEl: HTMLElement;
  private readonly bannerTitle: HTMLElement;
  private readonly bannerSub: HTMLElement;
  private readonly flashEl: HTMLElement;
  private readonly speedEl: HTMLElement;
  private readonly glowEl: HTMLElement;
  private readonly hintEl: HTMLElement;
  private readonly ballArrowEl: HTMLElement;
  private readonly wreckedEl: HTMLElement;
  private readonly wreckedCount: HTMLElement;
  private readonly scoreboardEl: HTMLElement;
  private readonly replayEl: HTMLElement;
  private readonly replayScorer: HTMLElement;
  private readonly replaySpeed: HTMLElement;
  private readonly replayBar: HTMLElement;
  private readonly replaySkip: HTMLElement;
  private readonly drillEl: HTMLElement;
  private readonly drillName: HTMLElement;
  private readonly drillAttempt: HTMLElement;
  private readonly drillHits: HTMLElement;
  private readonly drillBar: HTMLElement;
  private readonly styleEl: HTMLStyleElement;

  private last: Partial<Record<string, string | number>> = {};

  constructor(container: HTMLElement, bindings: KeyBindings = DEFAULT_BINDINGS) {
    this.styleEl = document.createElement('style');
    this.styleEl.textContent = STYLE;
    document.head.appendChild(this.styleEl);

    this.root = document.createElement('div');
    this.root.className = 'rw-hud';
    this.root.innerHTML = `
      <div class="rw-top">
        <div class="rw-score rw-score-0" style="color:${cssHex(TEAM_COLORS[0])}"><span>0</span></div>
        <div class="rw-clock">
          <div class="rw-clock-time">5:00</div>
          <div class="rw-clock-label">Match</div>
        </div>
        <div class="rw-score rw-score-1" style="color:${cssHex(TEAM_COLORS[1])}"><span>0</span></div>
      </div>
      <div class="rw-boost">
        <svg viewBox="0 0 132 132" aria-hidden="true">
          <circle class="rw-boost-track" cx="66" cy="66" r="${BOOST_RADIUS}"></circle>
          <circle class="rw-boost-arc" cx="66" cy="66" r="${BOOST_RADIUS}"
            stroke-dasharray="${BOOST_CIRCUMFERENCE.toFixed(2)}"
            stroke-dashoffset="${BOOST_CIRCUMFERENCE.toFixed(2)}"></circle>
        </svg>
        <div class="rw-boost-value">0</div>
      </div>
      <div class="rw-speedo" style="opacity:0">
        <div class="rw-speedo-value">0</div>
        <div class="rw-speedo-label">uu/s</div>
      </div>
      <div class="rw-centre">
        <div class="rw-countdown">3</div>
        <div class="rw-banner">
          <div class="rw-banner-title">Goal</div>
          <div class="rw-banner-sub"></div>
        </div>
      </div>
      <div class="rw-replay">
        <div class="rw-replay-head"><span class="rw-replay-dot"></span>Replay</div>
        <div class="rw-replay-scorer">&nbsp;</div>
        <div class="rw-replay-stat">Shot <b>0</b> uu/s</div>
        <div class="rw-replay-bar"><i></i></div>
        <div class="rw-replay-skip">${REPLAY_SKIP_HINT}</div>
      </div>
      <div class="rw-drill" hidden>
        <div class="rw-drill-name"></div>
        <div class="rw-drill-stats">
          <span class="rw-drill-attempt">1 / 10</span>
          <span class="rw-drill-hits">0 hit</span>
        </div>
        <div class="rw-drill-bar"><i></i></div>
      </div>
      <div class="rw-sb"></div>
      <div class="rw-wrecked">
        <div class="rw-wrecked-title">Wrecked</div>
        <div class="rw-wrecked-sub">Respawning in <b>0</b></div>
      </div>
      <div class="rw-ball-arrow">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2 L20 20 L12 15.6 L4 20 Z" fill="currentColor"></path>
        </svg>
      </div>
      <div class="rw-speed"></div>
      <div class="rw-glow"></div>
      <div class="rw-flash"></div>
      <div class="rw-hint"></div>
    `;
    container.appendChild(this.root);

    const q = <T extends Element>(selector: string): T => {
      const el = this.root.querySelector(selector);
      if (!el) throw new Error(`HUD element missing: ${selector}`);
      return el as T;
    };
    this.scoreEls = [q<HTMLElement>('.rw-score-0 span'), q<HTMLElement>('.rw-score-1 span')];
    this.clockEl = q<HTMLElement>('.rw-clock-time');
    this.clockLabel = q<HTMLElement>('.rw-clock-label');
    this.boostEl = q<HTMLElement>('.rw-boost');
    this.boostArc = q<SVGCircleElement>('.rw-boost-arc');
    this.boostValue = q<HTMLElement>('.rw-boost-value');
    this.speedoEl = q<HTMLElement>('.rw-speedo');
    this.speedoValue = q<HTMLElement>('.rw-speedo-value');
    this.speedoLabel = q<HTMLElement>('.rw-speedo-label');
    this.countdownEl = q<HTMLElement>('.rw-countdown');
    this.bannerEl = q<HTMLElement>('.rw-banner');
    this.bannerTitle = q<HTMLElement>('.rw-banner-title');
    this.bannerSub = q<HTMLElement>('.rw-banner-sub');
    this.flashEl = q<HTMLElement>('.rw-flash');
    this.speedEl = q<HTMLElement>('.rw-speed');
    this.glowEl = q<HTMLElement>('.rw-glow');
    this.hintEl = q<HTMLElement>('.rw-hint');
    this.ballArrowEl = q<HTMLElement>('.rw-ball-arrow');
    this.wreckedEl = q<HTMLElement>('.rw-wrecked');
    this.wreckedCount = q<HTMLElement>('.rw-wrecked-sub b');
    this.scoreboardEl = q<HTMLElement>('.rw-sb');
    this.replayEl = q<HTMLElement>('.rw-replay');
    this.replayScorer = q<HTMLElement>('.rw-replay-scorer');
    this.replaySpeed = q<HTMLElement>('.rw-replay-stat b');
    this.replayBar = q<HTMLElement>('.rw-replay-bar i');
    this.replaySkip = q<HTMLElement>('.rw-replay-skip');
    this.drillEl = q<HTMLElement>('.rw-drill');
    this.drillName = q<HTMLElement>('.rw-drill-name');
    this.drillAttempt = q<HTMLElement>('.rw-drill-attempt');
    this.drillHits = q<HTMLElement>('.rw-drill-hits');
    this.drillBar = q<HTMLElement>('.rw-drill-bar i');
    this.setBindings(bindings);
  }

  /**
   * The practice panel, or null to hide it. Fed every tick of a drill.
   *
   * `remaining` is the attempt clock as a fraction, scaled rather than sized:
   * a width in percent re-lays-out the bar sixty times a second, and a
   * transform does not touch layout at all.
   */
  setDrill(state: DrillHudState | null): void {
    this.set('drillOn', state === null ? 'off' : 'on', () => {
      this.drillEl.hidden = state === null;
    });
    if (state === null) return;

    this.set('drillName', state.name, () => {
      this.drillName.textContent = state.name;
    });
    const attempt = `${state.attempt} / ${state.attempts}`;
    this.set('drillAttempt', attempt, () => {
      this.drillAttempt.textContent = attempt;
    });
    const hits = `${state.hits} hit`;
    this.set('drillHits', hits, () => {
      this.drillHits.textContent = hits;
    });
    // Quantized to a percent: the bar is 176 px wide, so anything finer is a
    // write per tick that changes no pixel.
    const remaining = Math.round(Math.max(0, Math.min(1, state.remaining)) * 100);
    this.set('drillBar', remaining, () => {
      this.drillBar.style.transform = `scaleX(${remaining / 100})`;
      this.drillEl.classList.toggle('low', remaining <= 25);
    });
  }

  /** Re-render the control hints. Called whenever a rebind is applied, so the
   *  strip cannot go stale while a match is running. */
  setBindings(bindings: KeyBindings): void {
    const text = controlHintText(bindings);
    this.set('hint', text, () => {
      this.hintEl.textContent = text;
    });
  }

  /**
   * Move the HUD out from under a pair of thumbs.
   *
   * Called by the session whenever the on-screen controls are mounted or
   * dropped (see src/input/touch.ts). Everything it changes is a data
   * attribute the stylesheet reads — except the skip line, which is a
   * SENTENCE and not a layout: "press any key" is an instruction a phone
   * cannot follow, and the replay is skippable by any touch.
   */
  setTouchLayout(on: boolean): void {
    this.set('touchLayout', on ? 'on' : 'off', () => {
      if (on) this.root.dataset.touch = 'on';
      else delete this.root.dataset.touch;
      this.replaySkip.textContent = on ? 'Tap anywhere to skip' : REPLAY_SKIP_HINT;
    });
  }

  /** Write only what changed. */
  private set(key: string, value: string | number, apply: () => void): void {
    if (this.last[key] === value) return;
    this.last[key] = value;
    apply();
  }

  update(state: HudState): void {
    this.set('score0', state.score[0], () => {
      this.scoreEls[0].textContent = String(state.score[0]);
    });
    this.set('score1', state.score[1], () => {
      this.scoreEls[1].textContent = String(state.score[1]);
    });
    this.set('clock', state.clock, () => {
      this.clockEl.textContent = state.clock;
    });
    const tone = clockTone(state.clockSeconds, state.overtime, state.phase);
    this.set('clockTone', tone, () => {
      this.clockEl.classList.toggle('late', tone === 'late');
      this.clockEl.classList.toggle('critical', tone === 'critical');
    });

    const label =
      state.phase === 'ended'
        ? 'Final'
        : state.overtime
          ? 'Overtime'
          : state.phase === 'countdown'
            ? 'Kickoff'
            : 'Match';
    this.set('clockLabel', label, () => {
      this.clockLabel.textContent = label;
    });

    const boost = state.boost === null ? -1 : formatBoost(state.boost);
    const prevBoost = this.last['boost'];
    this.set('boost', boost, () => {
      this.boostEl.style.display = boost < 0 ? 'none' : '';
      if (boost < 0) return;
      if (boostPickedUp(typeof prevBoost === 'number' ? prevBoost : undefined, boost)) {
        this.boostEl.classList.remove('pop');
        void this.boostEl.offsetWidth;
        this.boostEl.classList.add('pop');
      }
      this.boostValue.textContent = String(boost);
      this.boostArc.setAttribute(
        'stroke-dashoffset',
        boostDashOffset(boost, BOOST_CIRCUMFERENCE).toFixed(2)
      );
      // The ring goes white-hot at full, which is the tell that a boost pickup
      // actually topped you up.
      this.boostArc.style.stroke = boost >= 100 ? '#fff3c4' : '#ffc23a';
    });

    // The speedometer diffs on the rounded value plus the supersonic flag, so
    // a car cruising at a steady speed writes nothing.
    const speedo = state.speedo;
    const speedoKey = speedo ? `${formatSpeed(speedo.speed)}:${speedo.supersonic ? 1 : 0}` : '';
    this.set('speedo', speedoKey, () => {
      if (!speedo) {
        this.speedoEl.style.opacity = '0';
        return;
      }
      const view = speedoReadout(speedo.speed, speedo.supersonic);
      this.speedoEl.style.opacity = '1';
      this.speedoValue.textContent = view.value;
      this.speedoLabel.textContent = view.label;
      this.speedoEl.classList.toggle('super', speedo.supersonic);
    });

    // Countdown pops on each whole number: scale is a pure function of the
    // number, so it re-triggers without any animation bookkeeping.
    const prevCountdown = this.last['countdown'];
    this.set('countdown', state.countdown, () => {
      const n = state.countdown;
      const text = countdownDisplay(n, typeof prevCountdown === 'number' ? prevCountdown : undefined);
      this.countdownEl.classList.remove('go');
      this.countdownEl.textContent = text;
      if (text === 'GO') {
        // The keyframe animation owns opacity and transform for its 640ms;
        // clear the inline values so it can, and force a reflow so a GO
        // following a GO (two kickoffs eight seconds apart) restarts it.
        this.countdownEl.style.transition = 'none';
        this.countdownEl.style.opacity = '';
        this.countdownEl.style.transform = '';
        void this.countdownEl.offsetWidth;
        this.countdownEl.classList.add('go');
        return;
      }
      this.countdownEl.style.opacity = n > 0 ? '1' : '0';
      this.countdownEl.style.transform = n > 0 ? 'scale(1)' : 'scale(0.6)';
      this.countdownEl.style.transition = 'none';
      // Force a reflow so the next frame animates from the popped-in scale.
      void this.countdownEl.offsetWidth;
      this.countdownEl.style.transition = 'transform 380ms cubic-bezier(0.16,1,0.3,1), opacity 200ms';
      this.countdownEl.style.transform = n > 0 ? 'scale(0.86)' : 'scale(0.6)';
    });

    const goalKey = state.goal ? `${state.goal.team}:${Math.round(state.goal.speed)}` : '';
    this.set('goal', goalKey, () => {
      if (state.goal) {
        const team = state.goal.team;
        this.bannerTitle.textContent = 'Goal';
        this.bannerTitle.style.color = cssHex(TEAM_COLORS[team]);
        this.bannerSub.textContent = `${team === 0 ? 'Blue' : 'Orange'} · ${formatSpeed(
          state.goal.speed
        )} uu/s`;
        this.bannerEl.classList.add('on');
      } else {
        this.bannerEl.classList.remove('on');
      }
    });

    // Flash and speed lines change every frame by design, so they are written
    // through the same diff at 2 decimal places rather than not at all.
    const flash = Math.round(Math.max(0, Math.min(1, state.flash)) * 100) / 100;
    this.set('flash', flash, () => {
      this.flashEl.style.opacity = String(flash * 0.85);
    });
    const speed = Math.round(Math.max(0, Math.min(1, state.speed)) * 100) / 100;
    this.set('speed', speed, () => {
      this.speedEl.style.opacity = String(speed);
    });

    // The wash is tinted by `currentColor`, so the colour and the opacity are
    // two separate diffs — the tint changes once per goal, the opacity every
    // frame a shell is burning.
    const glow = Math.round(Math.max(0, Math.min(1, state.glow)) * 100) / 100;
    this.set('glow', glow, () => {
      this.glowEl.style.opacity = (glow * GLOW_CEILING).toFixed(3);
    });
    const glowTint = state.goal ? cssHex(TEAM_COLORS[state.goal.team]) : '#ffffff';
    this.set('glowTint', glowTint, () => {
      this.glowEl.style.color = glowTint;
    });

    // The card's text is diffed on its own key so the progress bar, which moves
    // every frame, does not rewrite the scorer's name 144 times a second.
    const replayKey = state.replay
      ? `${state.replay.scorer}:${formatSpeed(state.replay.speed)}`
      : '';
    this.set('replay', replayKey, () => {
      if (state.replay) {
        this.replayScorer.textContent = state.replay.scorer;
        this.replaySpeed.textContent = formatSpeed(state.replay.speed);
        this.replayEl.classList.add('on');
      } else {
        this.replayEl.classList.remove('on');
      }
    });
    const progress = state.replay
      ? Math.round(Math.max(0, Math.min(1, state.replay.progress)) * 100)
      : 0;
    this.set('replayProgress', progress, () => {
      this.replayBar.style.width = `${progress}%`;
    });

    // The markup is its own diff key: it changes only when a stat does, so a
    // board held open for ten seconds is written once rather than 1200 times.
    this.set('scoreboard', state.scoreboard ?? '', () => {
      const open = state.scoreboard !== null;
      this.scoreboardEl.classList.toggle('on', open);
      if (open) this.scoreboardEl.innerHTML = state.scoreboard as string;
    });

    // Diffed on the DISPLAYED numeral, not the raw float, so a timer ticking
    // down at 120 Hz writes to the DOM three times rather than 240.
    const respawn = respawnCountdown(state.respawnIn);
    this.set('respawn', respawn, () => {
      this.wreckedEl.classList.toggle('on', respawn !== '');
      if (respawn !== '') this.wreckedCount.textContent = respawn;
    });

    // The arrow slides every frame the ball is off screen, so it is diffed at
    // whole percents and whole degrees rather than on raw floats.
    const arrow = state.ballArrow;
    const arrowKey = arrow
      ? `${Math.round(arrow.x * 1000)}:${Math.round(arrow.y * 1000)}:${Math.round(arrow.angle)}`
      : '';
    this.set('ballArrow', arrowKey, () => {
      if (!arrow) {
        this.ballArrowEl.classList.remove('on');
        return;
      }
      this.ballArrowEl.style.left = `${(arrow.x * 100).toFixed(2)}%`;
      this.ballArrowEl.style.top = `${(arrow.y * 100).toFixed(2)}%`;
      this.ballArrowEl.style.transform = `rotate(${arrow.angle.toFixed(1)}deg)`;
      this.ballArrowEl.classList.add('on');
    });
  }

  dispose(): void {
    this.root.remove();
    this.styleEl.remove();
  }
}
